import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Fade,
  Slide,
  Zoom,
  Paper,
  Chip,
  LinearProgress,
  Avatar,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SpeedIcon from '@mui/icons-material/Speed';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import InsightsIcon from '@mui/icons-material/Insights';
import RouteIcon from '@mui/icons-material/Route';
import BusinessIcon from '@mui/icons-material/Business';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import PsychologyIcon from '@mui/icons-material/Psychology';
import BoltIcon from '@mui/icons-material/Bolt';
import PeopleIcon from '@mui/icons-material/People';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { Link } from 'react-router-dom';
import { useThemeMode } from '../contexts/ThemeModeContext';
import DemoRequestForm from '../components/DemoRequestForm';

const LandingPage: React.FC = () => {
  const { mode } = useThemeMode();
  const [chatStep, setChatStep] = useState(0);
  const [agentStep, setAgentStep] = useState(0);
  const [showTickets, setShowTickets] = useState(false);
  const [pulseIcons, setPulseIcons] = useState([0, 1, 2, 3]);
  const [demoFormOpen, setDemoFormOpen] = useState(false);

  useEffect(() => {
    const chatInterval = setInterval(() => {
      setChatStep((prev) => {
        const next = (prev + 1) % 6;
        if (next === 3) {
          setTimeout(() => setShowTickets(true), 800);
        } else {
          setShowTickets(false);
        }
        return next;
      });
    }, 2500);
    return () => clearInterval(chatInterval);
  }, []);

  useEffect(() => {
    const agentInterval = setInterval(() => {
      setAgentStep((prev) => (prev + 1) % 5);
    }, 3000);
    return () => clearInterval(agentInterval);
  }, []);

  useEffect(() => {
    const pulseInterval = setInterval(() => {
      setPulseIcons((prev) => prev.map((val, idx) => (idx === Math.floor(Math.random() * 4) ? 1 : Math.max(0, val - 0.1))));
    }, 1500);
    return () => clearInterval(pulseInterval);
  }, []);

  const handleGetStarted = () => {
    const { getConfig } = require('../config/environment');
    const config = getConfig();
    window.location.href = `${config.adminUrl}/register`;
  };

  const getBackgroundGradient = () => {
    if (mode === 'dark') {
      return 'linear-gradient(180deg, #0f172a 0%, #151b26 25%, #1a2332 50%, #1c2838 75%, #1e293b 100%)';
    }
    return 'linear-gradient(180deg, #FAFBFC 0%, #FAFAFC 25%, #F9FAFB 50%, #F9FAFC 75%, #F8FAFC 100%)';
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: getBackgroundGradient(),
      position: 'relative',
      overflowX: 'hidden',
      overflowY: 'auto',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: mode === 'dark'
          ? `radial-gradient(circle at 20% 80%, rgba(99,102,241,0.08) 0%, transparent 50%),
             radial-gradient(circle at 80% 20%, rgba(236,72,153,0.08) 0%, transparent 50%)`
          : `radial-gradient(circle at 20% 80%, rgba(99, 102, 241, 0.05) 0%, transparent 50%),
             radial-gradient(circle at 80% 20%, rgba(236, 72, 153, 0.05) 0%, transparent 50%)`,
        pointerEvents: 'none',
      }
    }}>
      {/* Header with Logo */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: { xs: 4, md: 6 }, pb: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            component="img"
            src="/atlasai-icon.svg"
            alt="CoreSupport"
            sx={{
              width: { xs: 40, md: 56 },
              height: { xs: 40, md: 56 },
            }}
          />
          <Box>
            <Typography
              variant="h4"
              component="div"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.5rem', md: '2rem' },
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif',
                color: mode === 'dark' ? '#ffffff' : '#0f172a',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              textRendering: 'optimizeLegibility',
                mb: 0.5,
                lineHeight: 1.2,
              }}
            >
              CoreSupport
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                fontWeight: 500,
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif',
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                lineHeight: 1.2,
              }}
            >
              Empower every employee. Accelerate every response.
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* Hero with Immediate Demo */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: { xs: 6, md: 8 }, pb: { xs: 8, md: 12 } }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Fade in timeout={600}>
              <Box>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                    fontWeight: 800,
                    lineHeight: 1.1,
                    color: mode === 'dark' ? '#ffffff' : '#0f172a',
                    mb: 2,
                    letterSpacing: '-0.02em',
                  }}
                >
                  Private and Secure AI — From Knowledge to Resolution.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    fontWeight: 400,
                    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#64748b',
                    mb: 3,
                    lineHeight: 1.6,
                  }}
                >
                  CoreSupport AI is a private and secure enterprise platform that unifies knowledge management and AI-driven support — empowering employees with answers and accelerating resolution across the organization.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleGetStarted}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
                      backgroundSize: '200% 200%',
                      boxShadow: '0 8px 32px rgba(99, 102, 241, 0.4)',
                      animation: 'gradientShift 3s ease infinite',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #db2777 100%)',
                        boxShadow: '0 12px 40px rgba(99, 102, 241, 0.5)',
                        transform: 'translateY(-2px) scale(1.02)',
                        backgroundSize: '200% 200%',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '@keyframes gradientShift': {
                        '0%, 100%': { backgroundPosition: '0% 50%' },
                        '50%': { backgroundPosition: '100% 50%' },
                      }
                    }}
                  >
                    Get Started
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: { xs: 3, md: 4 }, flexWrap: 'wrap', mt: 2 }}>
                  <Box
                    sx={{
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700, 
                        mb: 0.5,
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif',
                      }}
                    >
                      Dedicated Agent
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif',
                      }}
                    >
                      One AI expert for your organization
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: mode === 'dark' ? 'rgba(255,255,255,0.5)' : '#94a3b8',
                        fontSize: '0.75rem',
                        fontStyle: 'italic',
                        mt: 0.5,
                        display: 'block',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif',
                      }}
                    >
                      Preserves institutional knowledge, never forgets
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700, 
                        mb: 0.5,
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif',
                      }}
                    >
                      Trained on Your Knowledge
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif',
                      }}
                    >
                      Understands your workflows and policies
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: mode === 'dark' ? 'rgba(255,255,255,0.5)' : '#94a3b8',
                        fontSize: '0.75rem',
                        fontStyle: 'italic',
                        mt: 0.5,
                        display: 'block',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif',
                      }}
                    >
                      Deployed in your environment, data never shared
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700, 
                        mb: 0.5,
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif',
                      }}
                    >
                      Deploy in Minutes
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif',
                      }}
                    >
                      Get started in minutes, not weeks
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: mode === 'dark' ? 'rgba(255,255,255,0.5)' : '#94a3b8',
                        fontSize: '0.75rem',
                        fontStyle: 'italic',
                        mt: 0.5,
                        display: 'block',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif',
                      }}
                    >
                      Reduces resolution times by up to 85%
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Fade>
          </Grid>
          
          {/* Live Chat Demo - Hero */}
          <Grid item xs={12} md={6}>
            <Zoom in timeout={800}>
              <Paper
                elevation={12}
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  background: mode === 'dark' 
                    ? 'rgba(15, 23, 42, 0.98)' 
                    : 'rgba(255, 255, 255, 0.98)',
                  border: mode === 'dark' ? '1px solid rgba(71, 85, 105, 0.4)' : '1px solid rgba(226, 232, 240, 0.8)',
                  boxShadow: mode === 'dark' 
                    ? '0 20px 60px rgba(0, 0, 0, 0.4)' 
                    : '0 20px 60px rgba(15, 23, 42, 0.15)',
                }}
              >
                <Box sx={{ 
                  p: 2.5, 
                  borderBottom: mode === 'dark' ? '1px solid rgba(71, 85, 105, 0.4)' : '1px solid rgba(226, 232, 240, 0.8)',
                  background: mode === 'dark' ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.05)',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
                      }}
                    >
                      <SmartToyIcon sx={{ fontSize: 24, color: '#fff' }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
                        Your Organization's AI Expert
                      </Typography>
                      <Typography variant="caption" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.6)' : '#94a3b8' }}>
                        Online • Trained on your knowledge base
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Box sx={{ p: 3, minHeight: '420px', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {/* User Message */}
                  <Box sx={{ alignSelf: 'flex-end', maxWidth: '75%' }}>
                    <Paper
                      elevation={2}
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        color: '#fff',
                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {chatStep === 0 && 'How do I reset my VPN password?'}
                        {chatStep === 1 && 'My laptop screen is flickering after the update'}
                        {chatStep === 2 && 'I need to install Adobe Creative Suite on my work laptop'}
                        {chatStep === 3 && 'I can\'t access the shared drive for my project'}
                        {chatStep === 4 && 'How do I set up my email on my new phone?'}
                        {chatStep === 5 && 'I need admin rights to install software'}
                      </Typography>
                    </Paper>
                  </Box>

                  {/* AI Response */}
                  {(chatStep >= 1 || chatStep === 0) && (
                    <Fade in={chatStep !== 0} timeout={600}>
                      <Box sx={{ alignSelf: 'flex-start', maxWidth: '80%' }}>
                        {chatStep === 0 && (
                          <Box sx={{ display: 'flex', gap: 0.5, px: 2, py: 1 }}>
                            {[0, 1, 2].map((i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  background: mode === 'dark' ? 'rgba(255,255,255,0.4)' : '#94a3b8',
                                  animation: 'typing 1.4s infinite',
                                  animationDelay: `${i * 0.2}s`,
                                  '@keyframes typing': {
                                    '0%, 60%, 100%': { transform: 'translateY(0)', opacity: 0.4 },
                                    '30%': { transform: 'translateY(-10px)', opacity: 1 },
                                  }
                                }}
                              />
                            ))}
                          </Box>
                        )}
                        {chatStep >= 1 && (
                          <Paper
                            elevation={2}
                            sx={{
                              p: 2.5,
                              borderRadius: 3,
                              background: mode === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(241, 245, 249, 0.9)',
                              border: '1px solid',
                              borderColor: mode === 'dark' ? 'rgba(71, 85, 105, 0.6)' : 'rgba(226, 232, 240, 0.8)',
                            }}
                          >
                            <Typography variant="body2" sx={{ color: mode === 'dark' ? '#fff' : '#0f172a', mb: 2, lineHeight: 1.7 }}>
                              {chatStep === 1 && 'You can reset your VPN password through the employee portal. Go to Settings → Security → Reset VPN Password. I can also generate a reset link for you.'}
                              {chatStep === 2 && 'This could be a display driver issue after the update. Try updating your display drivers first. If that doesn\'t work, I\'ll create a ticket for our IT team to investigate further.'}
                              {chatStep === 3 && 'I understand you need software installation. Per company policy, this requires IT approval and cannot be self-installed. Creating a ticket and routing it to the IT Support team now...'}
                              {chatStep === 4 && 'Check your network permissions in the employee portal. If you still can\'t access it, I\'ll escalate this to our IT team. What\'s the exact path of the shared drive?'}
                              {chatStep === 5 && 'Here are the steps to set up company email on your phone: [Link to KB article]. For advanced configuration, I can create a ticket for our IT team.'}
                            </Typography>
                            
                            {/* Show ticket creation for complex requests */}
                            {chatStep === 3 && showTickets && (
                              <Slide direction="up" in={showTickets} timeout={600}>
                                <Box sx={{ mt: 2, p: 2, borderRadius: 2, background: mode === 'dark' ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)', border: '1.5px solid #6366f1' }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#6366f1', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                                      TICKET T-1247 CREATED
                                    </Typography>
                                    <Chip 
                                      label="Routed to IT" 
                                      size="small" 
                                      sx={{ 
                                        background: '#6366f1', 
                                        color: '#fff', 
                                        height: 22,
                                        fontSize: '0.7rem',
                                      }} 
                                    />
                                  </Box>
                                  <Typography variant="caption" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569', fontSize: '0.75rem' }}>
                                    Software Installation Request • Assigned to IT Team
                                  </Typography>
                                </Box>
                              </Slide>
                            )}
                            
                            {/* Simple resolution badge */}
                            {(chatStep === 1 || chatStep === 2 || chatStep === 4 || chatStep === 5) && (
                              <Chip 
                                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                                label="Resolved" 
                                size="small" 
                                sx={{ 
                                  background: '#10b981',
                                  color: '#fff',
                                  mt: 1,
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </Paper>
                        )}
                      </Box>
                    </Fade>
                  )}
                </Box>

                {/* Input Box */}
                <Box sx={{ p: 2.5, borderTop: mode === 'dark' ? '1px solid rgba(71, 85, 105, 0.4)' : '1px solid rgba(226, 232, 240, 0.8)' }}>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Box
                      sx={{
                        flex: 1,
                        p: 1.5,
                        borderRadius: 3,
                        background: mode === 'dark' ? 'rgba(71, 85, 105, 0.3)' : 'rgba(241, 245, 249, 0.8)',
                        border: 'none',
                        color: mode === 'dark' ? 'rgba(255,255,255,0.5)' : '#94a3b8',
                        fontSize: '0.9rem',
                      }}
                    >
                      Type your message...
                    </Box>
                    <Button
                      sx={{
                        minWidth: 50,
                        height: 50,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                        '&:hover': { 
                          background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 16px rgba(99, 102, 241, 0.4)',
                        },
                      }}
                    >
                      <SendIcon sx={{ color: '#fff', fontSize: 20 }} />
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Zoom>
          </Grid>
        </Grid>
      </Container>

      {/* Organization-Specific Agents Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: { xs: 4, md: 6 }, pb: { xs: 8, md: 12 } }}>
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              fontWeight: 700,
              color: mode === 'dark' ? '#ffffff' : '#0f172a',
              mb: 2,
            }}
          >
            Expert Agents, Deployed for Your Organization
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#475569',
              fontSize: '1.1rem',
              maxWidth: '700px',
              mx: 'auto',
            }}
          >
            We deploy expert AI agents for your organization. Not a generic chatbot—an expert that learns from your knowledge base, policies, and workflows to become an expert on your organization. It captures your institutional knowledge, preventing knowledge loss and eliminating the time wasted on knowledge transfers.
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 3, md: 4 }}>
          {[
            {
              icon: BusinessIcon,
              title: 'Expert Agent for Your Organization',
              desc: 'Complete isolation. Your agent knows only your organization\'s knowledge, policies, and procedures.',
              color: '#6366f1',
            },
            {
              icon: MenuBookIcon,
              title: 'Trained on Your Knowledge Base',
              desc: 'Upload your documentation, policies, and workflows. Your agent learns from your knowledge and becomes an expert on how your organization operates—no knowledge loss, no time wasted on transfers.',
              color: '#8b5cf6',
            },
            {
              icon: RouteIcon,
              title: 'Smart Escalation Rules',
              desc: 'Configure routing rules specific to your organization. Your agent knows what requires IT, what needs approval, what can be self-served.',
              color: '#ec4899',
            },
            {
              icon: SmartToyIcon,
              title: 'Automatic Deployment',
              desc: 'Agent is provisioned automatically when you sign up. Trained and ready in minutes, not weeks.',
              color: '#10b981',
            },
          ].map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Zoom in timeout={600 + index * 100}>
                  <Box
                    sx={{
                      p: 4,
                      borderRadius: 4,
                      background: mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.6), rgba(30, 41, 59, 0.4))'
                        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))',
                      backdropFilter: 'blur(20px)',
                      border: mode === 'dark' ? '1px solid rgba(71, 85, 105, 0.4)' : '1px solid rgba(226, 232, 240, 0.8)',
                      textAlign: 'center',
                      transition: 'all 0.3s ease',
                      height: '100%',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: mode === 'dark' 
                          ? `0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 2px ${feature.color}40` 
                          : `0 20px 40px rgba(15, 23, 42, 0.1), 0 0 0 2px ${feature.color}30`,
                        borderColor: `${feature.color}40`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        width: 72,
                        height: 72,
                        mx: 'auto',
                        mb: 3,
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          width: 72,
                          height: 72,
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${feature.color}20, ${feature.color}40)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.3s ease',
                          animation: pulseIcons[index] > 0.5 
                            ? `iconPulse 2s ease-in-out infinite` 
                            : 'none',
                          '@keyframes iconPulse': {
                            '0%, 100%': {
                              transform: 'scale(1)',
                              boxShadow: `0 0 0 0 ${feature.color}40`,
                            },
                            '50%': {
                              transform: 'scale(1.05)',
                              boxShadow: `0 0 0 8px ${feature.color}00`,
                            },
                          },
                          '&:hover': {
                            transform: 'scale(1.1) rotate(5deg)',
                            background: `linear-gradient(135deg, ${feature.color}40, ${feature.color}60)`,
                          },
                        }}
                      >
                        <IconComponent sx={{ fontSize: 36, color: feature.color }} />
                      </Box>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a', mb: 2 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#475569', lineHeight: 1.7 }}>
                      {feature.desc}
                    </Typography>
                  </Box>
                </Zoom>
              </Grid>
            );
          })}
        </Grid>
        
        {/* CTA After Agent Features */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleGetStarted}
            sx={{
              px: 5,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            Get Your Expert Agent
          </Button>
        </Box>
      </Container>

      {/* AI Assists Human Agents - Animated */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: { xs: 4, md: 6 }, pb: { xs: 8, md: 12 } }}>
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              fontWeight: 700,
              color: mode === 'dark' ? '#ffffff' : '#0f172a',
              mb: 2,
            }}
          >
            Your Agents Don't Work Alone
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#475569',
              fontSize: '1.1rem',
              maxWidth: '700px',
              mx: 'auto',
            }}
          >
            When complex issues become tickets, AI assists your human agents with everything they need to resolve faster.
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="outlined"
            size="medium"
            onClick={handleGetStarted}
            sx={{
              px: 3,
              py: 1,
              borderRadius: 3,
              borderColor: '#6366f1',
              color: '#6366f1',
              '&:hover': {
                borderColor: '#4f46e5',
                background: mode === 'dark' ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.05)',
              },
            }}
          >
            See How It Works
          </Button>
        </Box>

        <Paper
          elevation={12}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            background: mode === 'dark' 
              ? 'rgba(15, 23, 42, 0.98)' 
              : 'rgba(255, 255, 255, 0.98)',
            border: mode === 'dark' ? '1px solid rgba(71, 85, 105, 0.4)' : '1px solid rgba(226, 232, 240, 0.8)',
            boxShadow: mode === 'dark' 
              ? '0 20px 60px rgba(0, 0, 0, 0.4)' 
              : '0 20px 60px rgba(15, 23, 42, 0.15)',
          }}
        >
          <Box sx={{ p: 3, borderBottom: mode === 'dark' ? '1px solid rgba(71, 85, 105, 0.4)' : '1px solid rgba(226, 232, 240, 0.8)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', width: 44, height: 44 }}>
                  SM
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
                    Sarah Martinez
                  </Typography>
                  <Typography variant="caption" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.6)' : '#94a3b8' }}>
                    IT Support Agent
                  </Typography>
                </Box>
              </Box>
              <Chip 
                label="Ticket T-1247" 
                sx={{ 
                  background: mode === 'dark' ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.1)',
                  color: '#6366f1',
                  fontWeight: 600,
                }} 
              />
            </Box>
          </Box>

          <Grid container>
            <Grid item xs={12} md={8} sx={{ p: 4, borderRight: { md: mode === 'dark' ? '1px solid rgba(71, 85, 105, 0.4)' : '1px solid rgba(226, 232, 240, 0.8)' } }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a', mb: 2 }}>
                Software Installation Request
              </Typography>
              <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#475569', mb: 3, lineHeight: 1.7 }}>
                User needs Adobe Creative Suite installed on their work laptop. Per company policy, software installation requires IT approval and cannot be self-served.
              </Typography>

              {agentStep >= 1 && (
                <Fade in timeout={600}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <SmartToyIcon sx={{ fontSize: 22, color: '#6366f1' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
                        AI Assistant Suggestions
                      </Typography>
                    </Box>
                    <Paper
                      elevation={2}
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        background: mode === 'dark' ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)',
                        border: '1.5px solid #6366f1',
                      }}
                    >
                      {agentStep === 1 && (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a', mb: 1.5 }}>
                            Similar Tickets Found (3)
                          </Typography>
                          <Typography variant="caption" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#475569', lineHeight: 1.7 }}>
                            T-1234, T-1189, T-1102 all involved Creative Suite installation. Average resolution time: 15 minutes. All required IT team assignment per escalation rules.
                          </Typography>
                        </Box>
                      )}
                      {agentStep === 2 && (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a', mb: 1.5 }}>
                            Resolution Steps
                          </Typography>
                          <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 1 } }}>
                            <li><Typography variant="caption" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#475569', lineHeight: 1.7 }}>Verify user has required license in company portal</Typography></li>
                            <li><Typography variant="caption" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#475569', lineHeight: 1.7 }}>Connect via remote access (RDP credentials in dashboard)</Typography></li>
                            <li><Typography variant="caption" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#475569', lineHeight: 1.7 }}>Run installation package from company software portal</Typography></li>
                            <li><Typography variant="caption" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#475569', lineHeight: 1.7 }}>Verify installation and update ticket status</Typography></li>
                          </Box>
                        </Box>
                      )}
                      {agentStep >= 3 && (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a', mb: 1.5 }}>
                            AI Summary
                          </Typography>
                          <Typography variant="caption" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#475569', lineHeight: 1.7 }}>
                            Standard software installation request. User has valid license, device meets system requirements. Follow standard installation workflow (estimated 15 minutes). Escalation to IT team was correct per company policy—software installation cannot be self-served.
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Box>
                </Fade>
              )}
            </Grid>

            <Grid item xs={12} md={4} sx={{ p: 4, background: mode === 'dark' ? 'rgba(99,102,241,0.05)' : 'rgba(99,102,241,0.02)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <AutoFixHighIcon sx={{ fontSize: 22, color: '#6366f1' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
                  AI Tools
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Chip 
                  icon={<InsightsIcon sx={{ fontSize: 18 }} />}
                  label="Find Similar Tickets" 
                  sx={{ 
                    justifyContent: 'flex-start',
                    height: 40,
                    background: agentStep >= 1 ? 'rgba(99,102,241,0.2)' : 'transparent',
                    border: '1.5px solid #6366f1',
                    color: '#6366f1',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: 'rgba(99,102,241,0.15)',
                    }
                  }}
                />
                <Chip 
                  icon={<AutoFixHighIcon sx={{ fontSize: 18 }} />}
                  label="Get Resolution Steps" 
                  sx={{ 
                    justifyContent: 'flex-start',
                    height: 40,
                    background: agentStep >= 2 ? 'rgba(99,102,241,0.2)' : 'transparent',
                    border: '1.5px solid #6366f1',
                    color: '#6366f1',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: 'rgba(99,102,241,0.15)',
                    }
                  }}
                />
                <Chip 
                  icon={<SmartToyIcon sx={{ fontSize: 18 }} />}
                  label="Generate Summary" 
                  sx={{ 
                    justifyContent: 'flex-start',
                    height: 40,
                    background: agentStep >= 3 ? 'rgba(99,102,241,0.2)' : 'transparent',
                    border: '1.5px solid #6366f1',
                    color: '#6366f1',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: 'rgba(99,102,241,0.15)',
                    }
                  }}
                />
                <Chip 
                  icon={<RouteIcon sx={{ fontSize: 18 }} />}
                  label="Check Escalation Rules" 
                  sx={{ 
                    justifyContent: 'flex-start',
                    height: 40,
                    background: agentStep >= 4 ? 'rgba(99,102,241,0.2)' : 'transparent',
                    border: '1.5px solid #6366f1',
                    color: '#6366f1',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: 'rgba(99,102,241,0.15)',
                    }
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* About Us / Who We Are */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: { xs: 4, md: 6 }, pb: { xs: 8, md: 12 } }}>
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              fontWeight: 700,
              color: mode === 'dark' ? '#ffffff' : '#0f172a',
              mb: 2,
            }}
          >
            Built for Organizations That Need Real Support Infrastructure
          </Typography>
        </Box>
        <Grid container spacing={{ xs: 3, md: 4 }}>
          <Grid item xs={12} md={6}>
            <Zoom in timeout={600}>
              <Box
                sx={{
                  p: 5,
                  borderRadius: 4,
                  background: mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.6), rgba(30, 41, 59, 0.4))'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))',
                  backdropFilter: 'blur(20px)',
                  border: mode === 'dark' ? '1px solid rgba(71, 85, 105, 0.4)' : '1px solid rgba(226, 232, 240, 0.8)',
                  height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: mode === 'dark'
                      ? '0 12px 32px rgba(0, 0, 0, 0.3)'
                      : '0 12px 32px rgba(15, 23, 42, 0.1)',
                  },
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, color: mode === 'dark' ? '#fff' : '#0f172a', mb: 2 }}>
                  Who We Are
                </Typography>
                <Typography variant="body1" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569', lineHeight: 1.8, mb: 3 }}>
                  Your team spends too much time answering the same questions and handling routine requests. Core Support fixes that. You get a dedicated AI agent that knows your company's policies, workflows, and how things actually get done. It's the same enterprise-grade tech the big players use, without the enterprise price tag.
                </Typography>
                <Typography variant="body1" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569', lineHeight: 1.8 }}>
                  Simple questions? Handled instantly. Complex issues? Tickets get created and routed automatically. Your support team gets AI-powered help finding similar cases, suggested solutions, and summaries—so they can focus on the work that matters. Most importantly, your agent learns from your knowledge base and becomes an expert on your organization. No knowledge loss when people leave, no time wasted on knowledge transfers. Your institutional knowledge is preserved and instantly accessible. Everything stays private, and it all adapts to how your organization actually operates.
                </Typography>
              </Box>
            </Zoom>
          </Grid>
          <Grid item xs={12} md={6}>
            <Zoom in timeout={800}>
              <Box
                sx={{
                  p: 5,
                  borderRadius: 4,
                  background: mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.6), rgba(30, 41, 59, 0.4))'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))',
                  backdropFilter: 'blur(20px)',
                  border: mode === 'dark' ? '1px solid rgba(71, 85, 105, 0.4)' : '1px solid rgba(226, 232, 240, 0.8)',
                  height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: mode === 'dark'
                      ? '0 12px 32px rgba(0, 0, 0, 0.3)'
                      : '0 12px 32px rgba(15, 23, 42, 0.1)',
                  },
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, color: mode === 'dark' ? '#fff' : '#0f172a', mb: 4 }}>
                  What We Do
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Deploy AI Expert Card */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(99,102,241,0.06))'
                        : 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(99,102,241,0.03))',
                      border: `1.5px solid ${mode === 'dark' ? 'rgba(99,102,241,0.3)' : 'rgba(99,102,241,0.2)'}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateX(4px)',
                        borderColor: mode === 'dark' ? 'rgba(99,102,241,0.5)' : 'rgba(99,102,241,0.4)',
                        boxShadow: mode === 'dark'
                          ? '0 8px 24px rgba(99,102,241,0.2)'
                          : '0 8px 24px rgba(99,102,241,0.15)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                      <Box
                        sx={{
                          minWidth: 48,
                          height: 48,
                          borderRadius: 2.5,
                          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 12px rgba(99,102,241,0.3)',
                          position: 'relative',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            borderRadius: 2.5,
                            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                            opacity: 0,
                            animation: 'iconGlow 3s ease-in-out infinite',
                            '@keyframes iconGlow': {
                              '0%, 100%': { opacity: 0, transform: 'scale(1)' },
                              '50%': { opacity: 0.5, transform: 'scale(1.1)' },
                            },
                          },
                        }}
                      >
                        <RocketLaunchIcon sx={{ color: '#fff', fontSize: 24, position: 'relative', zIndex: 1 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            mb: 1,
                            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif',
                          }}
                        >
                          Deploy Your AI Expert
                        </Typography>
                        <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569', lineHeight: 1.7 }}>
                          We set up your dedicated AI agent and train it on your documentation, policies, and real workflows. It learns from your knowledge base to become an expert on your organization. No knowledge loss when team members leave—your agent preserves and applies your institutional knowledge, eliminating time wasted on knowledge transfers.
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  {/* Automate the Routine Card */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(139,92,246,0.12), rgba(139,92,246,0.06))'
                        : 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(139,92,246,0.03))',
                      border: `1.5px solid ${mode === 'dark' ? 'rgba(139,92,246,0.3)' : 'rgba(139,92,246,0.2)'}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateX(4px)',
                        borderColor: mode === 'dark' ? 'rgba(139,92,246,0.5)' : 'rgba(139,92,246,0.4)',
                        boxShadow: mode === 'dark'
                          ? '0 8px 24px rgba(139,92,246,0.2)'
                          : '0 8px 24px rgba(139,92,246,0.15)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                      <Box
                        sx={{
                          minWidth: 48,
                          height: 48,
                          borderRadius: 2.5,
                          background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 12px rgba(139,92,246,0.3)',
                          position: 'relative',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            borderRadius: 2.5,
                            background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)',
                            opacity: 0,
                            animation: 'iconGlow2 3.5s ease-in-out infinite 0.5s',
                            '@keyframes iconGlow2': {
                              '0%, 100%': { opacity: 0, transform: 'scale(1)' },
                              '50%': { opacity: 0.5, transform: 'scale(1.1)' },
                            },
                          },
                        }}
                      >
                        <SpeedIcon sx={{ color: '#fff', fontSize: 24, position: 'relative', zIndex: 1 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            mb: 1,
                            background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif',
                          }}
                        >
                          Automate the Routine
                        </Typography>
                        <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569', lineHeight: 1.7 }}>
                          Simple questions get answered instantly. When something needs human attention, tickets get created and routed to the right team automatically. Your people only deal with what actually matters.
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  {/* Supercharge Your Team Card */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(236,72,153,0.12), rgba(236,72,153,0.06))'
                        : 'linear-gradient(135deg, rgba(236,72,153,0.08), rgba(236,72,153,0.03))',
                      border: `1.5px solid ${mode === 'dark' ? 'rgba(236,72,153,0.3)' : 'rgba(236,72,153,0.2)'}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateX(4px)',
                        borderColor: mode === 'dark' ? 'rgba(236,72,153,0.5)' : 'rgba(236,72,153,0.4)',
                        boxShadow: mode === 'dark'
                          ? '0 8px 24px rgba(236,72,153,0.2)'
                          : '0 8px 24px rgba(236,72,153,0.15)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                      <Box
                        sx={{
                          minWidth: 48,
                          height: 48,
                          borderRadius: 2.5,
                          background: 'linear-gradient(135deg, #ec4899, #f472b6)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 12px rgba(236,72,153,0.3)',
                          position: 'relative',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            borderRadius: 2.5,
                            background: 'linear-gradient(135deg, #ec4899, #f472b6)',
                            opacity: 0,
                            animation: 'iconGlow3 4s ease-in-out infinite 1s',
                            '@keyframes iconGlow3': {
                              '0%, 100%': { opacity: 0, transform: 'scale(1)' },
                              '50%': { opacity: 0.5, transform: 'scale(1.1)' },
                            },
                          },
                        }}
                      >
                        <TrendingUpIcon sx={{ color: '#fff', fontSize: 24, position: 'relative', zIndex: 1 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            mb: 1,
                            background: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif',
                          }}
                        >
                          Supercharge Your Team
                        </Typography>
                        <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569', lineHeight: 1.7 }}>
                          When your agents tackle tickets, AI pulls up similar cases, suggests fixes, and writes summaries. They work faster, so your whole organization moves faster.
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              </Box>
            </Zoom>
          </Grid>
        </Grid>
      </Container>

      {/* Knowledge Base Demo */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 10 } }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Slide direction="right" in timeout={1000}>
              <Paper
                elevation={12}
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  background: mode === 'dark' 
                    ? 'rgba(15, 23, 42, 0.98)' 
                    : 'rgba(255, 255, 255, 0.98)',
                  border: mode === 'dark' ? '1px solid rgba(71, 85, 105, 0.4)' : '1px solid rgba(226, 232, 240, 0.8)',
                  boxShadow: mode === 'dark' 
                    ? '0 20px 60px rgba(0, 0, 0, 0.4)' 
                    : '0 20px 60px rgba(15, 23, 42, 0.15)',
                  height: { xs: '400px', md: '500px' },
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ 
                  p: 2.5, 
                  borderBottom: mode === 'dark' ? '1px solid rgba(71, 85, 105, 0.4)' : '1px solid rgba(226, 232, 240, 0.8)',
                  background: mode === 'dark' ? 'rgba(139,92,246,0.1)' : 'rgba(139,92,246,0.05)',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <MenuBookIcon sx={{ fontSize: 28, color: '#8b5cf6' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
                      Knowledge Base
                    </Typography>
                    <Chip label="Indexing" size="small" sx={{ ml: 'auto', backgroundColor: '#8b5cf6', color: '#fff' }} />
                  </Box>
                </Box>
                <Box sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', gap: 3, overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Paper
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: mode === 'dark' ? 'rgba(139,92,246,0.1)' : 'rgba(139,92,246,0.08)',
                        border: '1.5px solid #8b5cf6',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <CloudUploadIcon 
                        sx={{ 
                          color: '#8b5cf6', 
                          fontSize: 24,
                          animation: 'uploadFloat 3s ease-in-out infinite',
                          '@keyframes uploadFloat': {
                            '0%, 100%': { transform: 'translateY(0px)' },
                            '50%': { transform: 'translateY(-3px)' },
                          },
                        }} 
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a', mb: 0.5 }}>
                          Employee Handbook.pdf
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={75} 
                          sx={{ 
                            height: 6, 
                            borderRadius: 3,
                            backgroundColor: mode === 'dark' ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.1)',
                            '& .MuiLinearProgress-bar': {
                              background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)',
                              animation: 'progressPulse 2s ease-in-out infinite',
                              '@keyframes progressPulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.8 },
                              },
                            }
                          }} 
                        />
                      </Box>
                    </Paper>
                    <Paper
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: mode === 'dark' ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.08)',
                        border: '1.5px solid #6366f1',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <CheckCircleIcon 
                        sx={{ 
                          color: '#6366f1', 
                          fontSize: 24,
                          animation: 'checkPulse 2.5s ease-in-out infinite',
                          '@keyframes checkPulse': {
                            '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                            '50%': { transform: 'scale(1.05)', opacity: 0.9 },
                          },
                        }} 
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a', mb: 0.5 }}>
                          IT Policies.docx
                        </Typography>
                        <Typography variant="caption" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.6)' : '#64748b' }}>
                          Indexed • 156 chunks
                        </Typography>
                      </Box>
                    </Paper>
                    <Paper
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: mode === 'dark' ? 'rgba(139,92,246,0.1)' : 'rgba(139,92,246,0.08)',
                        border: '1.5px solid #8b5cf6',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <SearchIcon 
                        sx={{ 
                          color: '#8b5cf6', 
                          fontSize: 24,
                          animation: 'searchPulse 2s ease-in-out infinite',
                          '@keyframes searchPulse': {
                            '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                            '50%': { transform: 'scale(1.08)', opacity: 0.85 },
                          },
                        }} 
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a', mb: 0.5 }}>
                          Workflow Guides
                        </Typography>
                        <Typography variant="caption" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.6)' : '#64748b' }}>
                          Ready • Instant retrieval enabled
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>
                  <Box sx={{ 
                    mt: 'auto', 
                    p: 2, 
                    borderRadius: 2, 
                    background: mode === 'dark' ? 'rgba(16,185,129,0.1)' : 'rgba(16,185,129,0.08)',
                    border: '1.5px solid #10b981',
                    animation: 'successPulse 3s ease-in-out infinite',
                    '@keyframes successPulse': {
                      '0%, 100%': { 
                        borderColor: '#10b981',
                        boxShadow: '0 0 0 0 rgba(16,185,129,0)',
                      },
                      '50%': { 
                        borderColor: '#34d399',
                        boxShadow: '0 0 0 4px rgba(16,185,129,0.2)',
                      },
                    },
                  }}>
                    <Typography variant="caption" sx={{ color: mode === 'dark' ? '#34d399' : '#10b981', fontWeight: 600 }}>
                      ✓ Your AI agent is now trained on 3 documents and ready to answer questions instantly
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Slide>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                  fontWeight: 700,
                  color: mode === 'dark' ? '#ffffff' : '#0f172a',
                  mb: 3,
                }}
              >
                Your Knowledge, Instantly Accessible
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#475569',
                  fontSize: '1.1rem',
                  mb: 3,
                  lineHeight: 1.7,
                }}
              >
                Upload your documentation, policies, and workflows. Your AI agent learns from everything you provide, becoming an expert on your organization. No more knowledge loss when team members leave, no time wasted training new hires on workflows. Your agent captures and applies your institutional knowledge instantly—no generic responses, just expert answers based on how your organization actually works.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569' }}>
                    Automatic indexing and chunking
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569' }}>
                    Instant retrieval for accurate answers
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569' }}>
                    Always up-to-date with your latest docs
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569' }}>
                    Prevents knowledge loss and eliminates knowledge transfer overhead
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* What This Means for Your Organization */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: { xs: 8, md: 12 }, pb: { xs: 8, md: 12 } }}>
        <Fade in timeout={1000}>
          <Box>
            <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '3rem' },
                  fontWeight: 800,
                  color: mode === 'dark' ? '#ffffff' : '#0f172a',
                  mb: 2,
                  background: mode === 'dark'
                    ? 'linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.9) 100%)'
                    : 'linear-gradient(135deg, #0f172a 0%, #475569 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                What This Means for Your Organization
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
                  fontSize: '1.1rem',
                  maxWidth: '700px',
                  mx: 'auto',
                }}
              >
                Real outcomes that matter to your team and mission
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {/* Faster Resolutions */}
              <Grid item xs={12} md={6} lg={4}>
                <Fade in timeout={800}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      background: mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                      border: `1px solid ${mode === 'dark' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)'}`,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: mode === 'dark' 
                          ? '0 12px 40px rgba(99, 102, 241, 0.3)'
                          : '0 12px 40px rgba(99, 102, 241, 0.15)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <BoltIcon sx={{ 
                        fontSize: 40, 
                        color: '#6366f1',
                        mr: 2,
                      }} />
                      <Typography variant="h5" sx={{ fontWeight: 700, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
                        Faster Resolutions
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b', lineHeight: 1.7 }}>
                      Reduce response times by up to 85% while maintaining quality and accuracy
                    </Typography>
                  </Paper>
                </Fade>
              </Grid>

              {/* Preserved Knowledge */}
              <Grid item xs={12} md={6} lg={4}>
                <Fade in timeout={900}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      background: mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                      border: `1px solid ${mode === 'dark' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)'}`,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: mode === 'dark' 
                          ? '0 12px 40px rgba(99, 102, 241, 0.3)'
                          : '0 12px 40px rgba(99, 102, 241, 0.15)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PsychologyIcon sx={{ 
                        fontSize: 40, 
                        color: '#8b5cf6',
                        mr: 2,
                      }} />
                      <Typography variant="h5" sx={{ fontWeight: 700, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
                        Preserved Knowledge
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b', lineHeight: 1.7 }}>
                      Transform tribal knowledge into searchable, AI-ready intelligence that never walks out the door
                    </Typography>
                  </Paper>
                </Fade>
              </Grid>

              {/* Empowered Teams */}
              <Grid item xs={12} md={6} lg={4}>
                <Fade in timeout={1000}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      background: mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                      border: `1px solid ${mode === 'dark' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)'}`,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: mode === 'dark' 
                          ? '0 12px 40px rgba(99, 102, 241, 0.3)'
                          : '0 12px 40px rgba(99, 102, 241, 0.15)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PeopleIcon sx={{ 
                        fontSize: 40, 
                        color: '#ec4899',
                        mr: 2,
                      }} />
                      <Typography variant="h5" sx={{ fontWeight: 700, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
                        Empowered Teams
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b', lineHeight: 1.7 }}>
                      Free staff from repetitive work to focus on high-impact initiatives that truly matter
                    </Typography>
                  </Paper>
                </Fade>
              </Grid>

              {/* Seamless Integration */}
              <Grid item xs={12} md={6} lg={4}>
                <Fade in timeout={1100}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      background: mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                      border: `1px solid ${mode === 'dark' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)'}`,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: mode === 'dark' 
                          ? '0 12px 40px rgba(99, 102, 241, 0.3)'
                          : '0 12px 40px rgba(99, 102, 241, 0.15)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <IntegrationInstructionsIcon sx={{ 
                        fontSize: 40, 
                        color: '#10b981',
                        mr: 2,
                      }} />
                      <Typography variant="h5" sx={{ fontWeight: 700, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
                        Seamless Integration
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b', lineHeight: 1.7 }}>
                      Works with your existing ITSM, ticketing, and enterprise systems—no disruption
                    </Typography>
                  </Paper>
                </Fade>
              </Grid>

              {/* Reduced Burnout */}
              <Grid item xs={12} md={6} lg={4}>
                <Fade in timeout={1200}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      background: mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                      border: `1px solid ${mode === 'dark' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)'}`,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: mode === 'dark' 
                          ? '0 12px 40px rgba(99, 102, 241, 0.3)'
                          : '0 12px 40px rgba(99, 102, 241, 0.15)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <FavoriteBorderIcon sx={{ 
                        fontSize: 40, 
                        color: '#f43f5e',
                        mr: 2,
                      }} />
                      <Typography variant="h5" sx={{ fontWeight: 700, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
                        Reduced Burnout
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b', lineHeight: 1.7 }}>
                      Lighten workloads and improve team well-being by handling the heavy lifting
                    </Typography>
                  </Paper>
                </Fade>
              </Grid>

              {/* Elevated Performance */}
              <Grid item xs={12} md={6} lg={4}>
                <Fade in timeout={1300}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      background: mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                      border: `1px solid ${mode === 'dark' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)'}`,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: mode === 'dark' 
                          ? '0 12px 40px rgba(99, 102, 241, 0.3)'
                          : '0 12px 40px rgba(99, 102, 241, 0.15)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <TrendingUpIcon sx={{ 
                        fontSize: 40, 
                        color: '#f59e0b',
                        mr: 2,
                      }} />
                      <Typography variant="h5" sx={{ fontWeight: 700, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
                        Elevated Performance
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b', lineHeight: 1.7 }}>
                      Give every team member AI-powered guidance to perform like seasoned experts
                    </Typography>
                  </Paper>
                </Fade>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      </Container>


      {/* Final CTA */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: { xs: 6, md: 10 }, pb: { xs: 6, md: 8 } }}>
        <Box>
          <Fade in timeout={800}>
            <Box
              sx={{
                textAlign: 'center',
                p: { xs: 5, md: 8 },
                borderRadius: 4,
                background: mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(139,92,246,0.1))'
                  : 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(139,92,246,0.05))',
                border: mode === 'dark' 
                  ? '2px solid rgba(99,102,241,0.3)' 
                  : '2px solid rgba(99,102,241,0.2)',
                backdropFilter: 'blur(20px)',
                boxShadow: mode === 'dark'
                  ? '0 8px 32px rgba(99, 102, 241, 0.15)'
                  : '0 8px 32px rgba(99, 102, 241, 0.1)',
              }}
            >
              <BusinessIcon sx={{ fontSize: 64, color: '#6366f1', mb: 3, opacity: 0.8 }} />
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                  fontWeight: 700,
                  color: mode === 'dark' ? '#ffffff' : '#0f172a',
                  mb: 2,
                }}
              >
                Ready to Give Your Team Time Back?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#475569',
                  mb: 4,
                  maxWidth: '700px',
                  mx: 'auto',
                  fontSize: '1.1rem',
                  lineHeight: 1.7,
                }}
              >
                Stop wasting time on repetitive support tasks. Register your organization and get your dedicated AI expert deployed in minutes—trained on your knowledge base, ready to handle simple questions instantly and route complex issues to your team automatically.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGetStarted}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.2rem',
                    fontWeight: 600,
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
                    backgroundSize: '200% 200%',
                    boxShadow: '0 12px 40px rgba(99, 102, 241, 0.4)',
                    animation: 'gradientShift 3s ease infinite',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #db2777 100%)',
                      boxShadow: '0 16px 48px rgba(99, 102, 241, 0.5)',
                      transform: 'translateY(-3px) scale(1.05)',
                      backgroundSize: '200% 200%',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '@keyframes gradientShift': {
                      '0%, 100%': { backgroundPosition: '0% 50%' },
                      '50%': { backgroundPosition: '100% 50%' },
                    }
                  }}
                >
                  Register Your Organization
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setDemoFormOpen(true);
                  }}
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.2rem',
                    fontWeight: 600,
                    borderRadius: 4,
                    borderColor: '#6366f1',
                    borderWidth: 2,
                    color: mode === 'dark' ? '#fff' : '#6366f1',
                    '&:hover': {
                      borderColor: '#4f46e5',
                      borderWidth: 2,
                      background: mode === 'dark' ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.05)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  Request a Demo
                </Button>
              </Box>
            </Box>
          </Fade>
        </Box>
      </Container>

      {/* Footer - Compact */}
      <Box
        sx={{
          borderTop: mode === 'dark' ? '1px solid rgba(71, 85, 105, 0.2)' : '1px solid rgba(226, 232, 240, 0.6)',
          py: 3,
          mt: { xs: 4, md: 6 },
          position: 'relative',
          zIndex: 1,
          background: mode === 'dark' 
            ? 'rgba(15, 23, 42, 0.3)'
            : 'rgba(248, 250, 252, 0.5)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            {/* Company Info - Single Line */}
            <Typography
              variant="caption"
              sx={{
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#94a3b8',
                fontSize: '0.75rem',
                textAlign: 'center',
              }}
            >
              Built by Gratitude Systems LLC • CBE/DBE/MBE Certified • Washington, DC
            </Typography>

            {/* Copyright */}
            <Typography
              variant="caption"
              sx={{
                textAlign: 'center',
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : '#94a3b8',
                fontSize: '0.75rem',
              }}
            >
              CoreSupport © {new Date().getFullYear()}
            </Typography>

            {/* Links */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Link
                to="/privacy"
                style={{
                  textDecoration: 'none',
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : '#94a3b8',
                  fontSize: '0.75rem',
                  transition: 'color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#6366f1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : '#94a3b8';
                }}
              >
                Privacy
              </Link>
              <Typography
                variant="caption"
                sx={{
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : '#cbd5e1',
                  fontSize: '0.75rem',
                }}
              >
                •
              </Typography>
              <Link
                to="/terms"
                style={{
                  textDecoration: 'none',
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : '#94a3b8',
                  fontSize: '0.75rem',
                  transition: 'color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#6366f1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : '#94a3b8';
                }}
              >
                Terms
              </Link>
              <Typography
                variant="caption"
                sx={{
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : '#cbd5e1',
                  fontSize: '0.75rem',
                }}
              >
                •
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : '#94a3b8',
                  fontSize: '0.75rem',
                }}
              >
                info@gratitudesystem.com
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Demo Request Form Modal */}
      <DemoRequestForm 
        open={demoFormOpen} 
        onClose={() => {
          setDemoFormOpen(false);
        }} 
        isPricingRequest={false}
      />
    </Box>
  );
};

export default LandingPage;
